import { arrayValue, isArray, snapshotArray } from '../array.js';
import type * as Core from '../../types/calculation-core.js';
import type { FinanceConfig, FinanceInput, InputCheck, BoundOptions, CompoundInterestConfig, InflationConfig, HousingLoanConfig, ConsumerLoanConfig, DepreciationConfig, DiscountConfig, RentIncreaseConfig, PosCommissionConfig, DiscountInput } from './finance-contracts.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
const FALLBACK_METADATA = Object.freeze(Object.fromEntries([
    'compound-interest',
    'inflation-value',
    'housing-loan',
    'consumer-loan',
    'depreciation-plan',
    'discount',
    'rent-increase',
    'pos-commission',
].map((calculatorId) => [calculatorId, Object.freeze({
        calculatorId,
        version: 'invalid-config',
        effectiveFrom: '1970-01-01',
        lastVerifiedAt: '1970-01-01',
        sources: Object.freeze([]),
        notes: Object.freeze(['Geçerli politika kaydı olmadan hesaplama yapılmadı.']),
    })] as const)));
function recordOf<T extends object>(value: T | null | undefined): T | null;
function recordOf(value: unknown): Readonly<Record<string, unknown>> | null;
function recordOf(value: unknown): object | null {
    return value !== null && typeof value === 'object' && !isArray(value)
        ? value
        : null;
}
function exactKeyErrors(value: unknown, required: readonly string[], optional: readonly string[], field: string, code: string): Core.InputError[] {
    const record = recordOf(value);
    if (record === null) {
        return [{ field, code, message: `${field} kapalı nesne sözleşmesiyle eşleşmelidir.` }];
    }
    const errors: Core.InputError[] = [];
    const allowed = new Set([...required, ...optional]);
    for (const key of Object.keys(record)) {
        if (!allowed.has(key)) {
            errors.push({ field: `${field}.${key}`, code, message: 'Sözleşmede bulunmayan alan kabul edilmez.' });
        }
    }
    for (const key of required) {
        if (!Object.prototype.hasOwnProperty.call(record, key)) {
            errors.push({
                field: `${field}.${key}`,
                code: code === 'INVALID_CONFIG' ? 'INVALID_CONFIG' : 'REQUIRED',
                message: 'Zorunlu alan eksiktir.',
            });
        }
    }
    return errors;
}
function invalidConfig(field: string, message: string = 'Konfigürasyon değeri geçersizdir.'): Core.InputError {
    return { field, code: 'INVALID_CONFIG', message };
}
function validateRange(field: string, value: unknown, minimumFloor: number, options: BoundOptions = {}) {
    const entries = arrayValue(value);
    if (entries === null || entries.length !== 2) {
        return invalidConfig(field, 'Alt ve üst sınır sonlu, sıralı iki sayı olmalıdır.');
    }
    const minimum = entries[0];
    const maximum = typeof minimum === 'number' && isFiniteNumber(minimum) ? entries[1] : undefined;
    if (typeof minimum !== 'number' || !isFiniteNumber(minimum) ||
        typeof maximum !== 'number' || !isFiniteNumber(maximum) ||
        (options.exclusiveMinimum === true ? minimum <= minimumFloor : minimum < minimumFloor) ||
        minimum > maximum ||
        (options.maximumCeiling !== undefined && maximum > options.maximumCeiling)) {
        return invalidConfig(field, 'Alt ve üst sınır sonlu, sıralı iki sayı olmalıdır.');
    }
    return undefined;
}
function validateMetadataExact(metadata: Core.CoreMetadata, calculatorId: string): Core.InputError[] {
    const errors = exactKeyErrors(metadata, ['calculatorId', 'version', 'effectiveFrom', 'lastVerifiedAt', 'sources'], ['effectiveTo', 'notes'], 'config.metadata', 'INVALID_CONFIG');
    const record = recordOf(metadata);
    if (record === null)
        return errors;
    errors.push(...validateMetadata(metadata));
    if (record.calculatorId !== calculatorId) {
        errors.push(invalidConfig('config.metadata.calculatorId', 'Hesap motoru kimliği değiştirilemez.'));
    }
    if (isArray(record.sources)) {
        record.sources.forEach((source: Core.CoreMetadata['sources'][number], index) => {
            errors.push(...exactKeyErrors(source, ['id', 'title', 'publisher', 'url', 'verifiedAt'], [], `config.metadata.sources[${index}]`, 'INVALID_CONFIG'));
        });
    }
    return errors;
}
function metadataFor(config: FinanceConfig, calculatorId: Core.FinanceBusinessToolId): Core.CoreMetadata {
    const record = recordOf(config);
    return resolveMetadata(record?.metadata, FALLBACK_METADATA[calculatorId]);
}
function validateConfigEnvelope(config: FinanceConfig, calculatorId: Core.FinanceBusinessToolId, requiredKeys: readonly string[]): Core.InputError[] {
    const errors = exactKeyErrors(config, ['metadata', ...requiredKeys], [], 'config', 'INVALID_CONFIG');
    const record = recordOf(config);
    if (record !== null)
        errors.push(...validateMetadataExact(record.metadata, calculatorId));
    return errors;
}
function boundedNumber(field: string, value: unknown, range: readonly [
    number,
    number
], options: BoundOptions = {}) {
    if (typeof value !== 'number' || !isFiniteNumber(value)) {
        return { field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` };
    }
    if ((options.exclusiveMinimum === true ? value <= range[0] : value < range[0]) ||
        (options.exclusiveMaximum === true ? value >= range[1] : value > range[1]) ||
        (options.integer === true && !Number.isInteger(value))) {
        return { field, code: 'OUT_OF_RANGE', message: `${field} izin verilen aralığın dışındadır.` };
    }
    return undefined;
}
function finiteResult<T>(value: T, numbers: readonly number[], metadata: Core.CoreMetadata) {
    return numbers.every(Number.isFinite)
        ? success(value, metadata)
        : failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Sonuç sonlu sayı aralığını aştı.',
            }], metadata);
}
function normalizeSignedZero(value: number): number {
    return Object.is(value, -0) ? 0 : value;
}
function requireInput<T extends object>(input: T, required: readonly string[], optional: readonly string[], metadata: Core.CoreMetadata): InputCheck<T> {
    const record = recordOf(input);
    if (record === null) {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Hesaplama girdileri gereklidir.' }], metadata);
    }
    return { record, errors: exactKeyErrors(record, required, optional, 'input', 'UNSUPPORTED_VALUE') };
}
function isFailureResult<T extends object>(value: InputCheck<T>): value is Extract<Core.Calculation<never>, {
    ok: false;
}> {
    return 'ok' in value && value.ok === false;
}
function policyNumber<T extends object, K extends keyof T & string>(record: T | null, field: K, minimum: number, maximum: number, integer: boolean = false) {
    const value = record?.[field];
    if (typeof value !== 'number' || !isFiniteNumber(value) || value < minimum || value > maximum || (integer && !Number.isInteger(value))) {
        return invalidConfig(`config.policy.${field}`);
    }
    return undefined;
}
function annuityPayment(principal: number, monthlyRate: number, termMonths: number) {
    if (monthlyRate === 0)
        return principal / termMonths;
    return principal * monthlyRate / -Math.expm1(-termMonths * Math.log1p(monthlyRate));
}
export function calculateCompoundInterest(input: FinanceInput<"compound-interest">, config: CompoundInterestConfig) {
    const metadata = metadataFor(config, 'compound-interest');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'compound-interest', ['principal', 'annualRatePercent', 'term', 'maximumTermYears', 'monthlyContribution', 'policy']);
    for (const field of ['principal', 'annualRatePercent', 'term', 'monthlyContribution'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], field === 'monthlyContribution' ? 0 : 0);
        if (error)
            configErrors.push(error);
    }
    const maximumTermYears = configRecord?.maximumTermYears;
    if (typeof maximumTermYears !== 'number' || !isFiniteNumber(maximumTermYears) ||
        maximumTermYears <= 0 ||
        maximumTermYears > 1000) {
        configErrors.push(invalidConfig('config.maximumTermYears'));
    }
    const policy = recordOf(configRecord?.policy);
    configErrors.push(...exactKeyErrors(policy, ['daysPerYear', 'monthsPerYear', 'annualPeriods', 'monthlyPeriods', 'dailyPeriods', 'withholdingScenario', 'inflationScenario'], [], 'config.policy', 'INVALID_CONFIG'));
    for (const [field, minimum, maximum, integer] of [
        ['daysPerYear', 1, 1000, true],
        ['monthsPerYear', 1, 100, true],
        ['annualPeriods', 1, 1000, true],
        ['monthlyPeriods', 1, 1000, true],
        ['dailyPeriods', 1, 1000, true],
    ] as const) {
        const error = policyNumber(policy, field, minimum, maximum, integer);
        if (error)
            configErrors.push(error);
    }
    for (const [field, rateKey] of [
        ['withholdingScenario', 'taxRate'],
        ['inflationScenario', 'annualInflationRate'],
    ] as const) {
        const scenarioValue = policy?.[field];
        if (scenarioValue === null)
            continue;
        const scenario = recordOf(scenarioValue);
        configErrors.push(...exactKeyErrors(scenario, ['label', rateKey], [], `config.policy.${field}`, 'INVALID_CONFIG'));
        if (typeof scenario?.label !== 'string' || scenario.label.trim() === '') {
            configErrors.push(invalidConfig(`config.policy.${field}.label`));
        }
        const rate = scenario === null ? undefined : rateKey === 'taxRate' && 'taxRate' in scenario ? scenario.taxRate : 'annualInflationRate' in scenario ? scenario.annualInflationRate : undefined;
        if (typeof rate !== 'number' || !isFiniteNumber(rate) || rate < 0 || rate > (field === 'withholdingScenario' ? 1 : 10)) {
            configErrors.push(invalidConfig(`config.policy.${field}.${rateKey}`));
        }
    }
    if (configErrors.length > 0 || configRecord === null || policy === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['principal', 'annualRatePercent', 'term', 'termUnit', 'compounding'], ['monthlyContribution'], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    const principalError = boundedNumber('principal', record.principal, config.principal);
    const rateError = boundedNumber('annualRatePercent', record.annualRatePercent, config.annualRatePercent);
    const termError = boundedNumber('term', record.term, config.term);
    const contribution = record.monthlyContribution ?? 0;
    const contributionError = boundedNumber('monthlyContribution', contribution, config.monthlyContribution);
    if (principalError)
        errors.push(principalError);
    if (rateError)
        errors.push(rateError);
    if (termError)
        errors.push(termError);
    if (contributionError)
        errors.push(contributionError);
    if (!['year', 'month', 'day'].includes(record.termUnit)) {
        errors.push({ field: 'termUnit', code: 'UNSUPPORTED_VALUE', message: 'Vade birimi desteklenmiyor.' });
    }
    if (!['annual', 'monthly', 'daily', 'continuous'].includes(record.compounding)) {
        errors.push({ field: 'compounding', code: 'UNSUPPORTED_VALUE', message: 'Bileşikleme sıklığı desteklenmiyor.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const principal = record.principal;
    const annualRatePercent = record.annualRatePercent;
    const term = record.term;
    const termUnit = record.termUnit;
    const compounding = record.compounding;
    const monthlyContribution = contribution;
    const termYears = termUnit === 'year'
        ? term
        : termUnit === 'month'
            ? term / config.policy.monthsPerYear
            : term / config.policy.daysPerYear;
    const rawContributionPaymentCount = termYears * config.policy.monthsPerYear;
    const crossFieldErrors = [];
    if (termYears > config.maximumTermYears) {
        crossFieldErrors.push({
            field: 'term',
            code: 'OUT_OF_RANGE',
            message: `Vade ${config.maximumTermYears} yıl üst sınırını aşamaz.`,
        });
    }
    if (monthlyContribution > 0 &&
        (termUnit === 'day' || !Number.isInteger(rawContributionPaymentCount))) {
        crossFieldErrors.push({
            field: 'monthlyContribution',
            code: 'UNSUPPORTED_VALUE',
            message: 'Aylık katkı için vade tam ay sayısı üretmeli ve gün birimi kullanılmamalıdır.',
        });
    }
    if (crossFieldErrors.length > 0)
        return failure(crossFieldErrors, metadata);
    const contributionPaymentCount = monthlyContribution > 0
        ? rawContributionPaymentCount
        : 0;
    const annualRate = annualRatePercent / 100;
    const periods = compounding === 'annual'
        ? config.policy.annualPeriods
        : compounding === 'monthly'
            ? config.policy.monthlyPeriods
            : config.policy.dailyPeriods;
    const annualLogGrowth = compounding === 'continuous'
        ? annualRate
        : periods * Math.log1p(annualRate / periods);
    const principalFutureValue = principal * Math.exp(annualLogGrowth * termYears);
    const monthlyLogGrowth = annualLogGrowth / config.policy.monthsPerYear;
    const contributionRate = Math.expm1(monthlyLogGrowth);
    let contributionFutureValue = 0;
    if (monthlyContribution > 0) {
        contributionFutureValue = contributionRate === 0
            ? monthlyContribution * contributionPaymentCount
            : monthlyContribution * Math.expm1(monthlyLogGrowth * contributionPaymentCount) / contributionRate;
    }
    const futureValue = principalFutureValue + contributionFutureValue;
    const totalInvested = principal + monthlyContribution * contributionPaymentCount;
    const totalInterest = futureValue - totalInvested;
    const effectiveAnnualRate = Math.expm1(annualLogGrowth);
    const contributionSimpleInterest = monthlyContribution * (contributionPaymentCount +
        annualRate * contributionPaymentCount * (contributionPaymentCount - 1) /
            (2 * config.policy.monthsPerYear));
    const simpleInterestValue = principal * (1 + annualRate * termYears)
        + contributionSimpleInterest;
    const withholdingScenario = config.policy.withholdingScenario === null
        ? null
        : {
            label: config.policy.withholdingScenario.label,
            taxRatePercent: normalizeSignedZero(config.policy.withholdingScenario.taxRate * 100),
            withholdingAmount: normalizeSignedZero(totalInterest * config.policy.withholdingScenario.taxRate),
            afterWithholdingValue: normalizeSignedZero(totalInvested + totalInterest * (1 - config.policy.withholdingScenario.taxRate)),
        };
    const inflationScenario = config.policy.inflationScenario === null
        ? null
        : (() => {
            const inflationAdjustedValue = futureValue /
                Math.pow(1 + config.policy.inflationScenario.annualInflationRate, termYears);
            return {
                label: config.policy.inflationScenario.label,
                annualInflationRatePercent: normalizeSignedZero(config.policy.inflationScenario.annualInflationRate * 100),
                inflationAdjustedValue: normalizeSignedZero(inflationAdjustedValue),
                realGainPositive: inflationAdjustedValue > totalInvested,
            };
        })();
    return finiteResult({
        principal: normalizeSignedZero(principal),
        annualRatePercent: normalizeSignedZero(annualRatePercent),
        term: normalizeSignedZero(term),
        termUnit,
        termYears: normalizeSignedZero(termYears),
        compounding,
        monthlyContribution: normalizeSignedZero(monthlyContribution),
        contributionTiming: 'end-of-month',
        contributionPaymentCount: normalizeSignedZero(contributionPaymentCount),
        totalInvested: normalizeSignedZero(totalInvested),
        contributionFutureValue: normalizeSignedZero(contributionFutureValue),
        totalInterest: normalizeSignedZero(totalInterest),
        futureValue: normalizeSignedZero(futureValue),
        effectiveAnnualRatePercent: normalizeSignedZero(effectiveAnnualRate * 100),
        simpleInterestValue: normalizeSignedZero(simpleInterestValue),
        compoundAdvantage: normalizeSignedZero(futureValue - simpleInterestValue),
        withholdingScenario,
        inflationScenario,
    }, [
        termYears,
        contributionPaymentCount,
        totalInvested,
        contributionFutureValue,
        totalInterest,
        futureValue,
        effectiveAnnualRate,
        simpleInterestValue,
        ...(withholdingScenario === null ? [] : [withholdingScenario.taxRatePercent, withholdingScenario.withholdingAmount, withholdingScenario.afterWithholdingValue]),
        ...(inflationScenario === null ? [] : [inflationScenario.annualInflationRatePercent, inflationScenario.inflationAdjustedValue]),
    ], metadata);
}
export function calculateInflationValue(input: FinanceInput<"inflation-value">, config: InflationConfig) {
    const metadata = metadataFor(config, 'inflation-value');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'inflation-value', ['amount', 'annualInflationPercent', 'years']);
    for (const field of ['amount', 'annualInflationPercent', 'years'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], 0);
        if (error)
            configErrors.push(error);
    }
    if (configErrors.length > 0 || configRecord === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['amount', 'annualInflationPercent', 'years'], [], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    for (const [field, range] of [
        ['amount', config.amount],
        ['annualInflationPercent', config.annualInflationPercent],
        ['years', config.years],
    ] as const) {
        const error = boundedNumber(field, record[field], range);
        if (error)
            errors.push(error);
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const amount = record.amount;
    const annualInflationPercent = record.annualInflationPercent;
    const years = record.years;
    const cumulativeFactor = Math.pow(1 + annualInflationPercent / 100, years);
    const futureEquivalent = amount * cumulativeFactor;
    const presentPurchasingPower = amount / cumulativeFactor;
    const purchasingPowerLoss = amount - presentPurchasingPower;
    return finiteResult({ amount: normalizeSignedZero(amount), annualInflationPercent: normalizeSignedZero(annualInflationPercent), years: normalizeSignedZero(years), cumulativeFactor: normalizeSignedZero(cumulativeFactor), futureEquivalent: normalizeSignedZero(futureEquivalent), presentPurchasingPower: normalizeSignedZero(presentPurchasingPower), purchasingPowerLoss: normalizeSignedZero(purchasingPowerLoss) }, [cumulativeFactor, futureEquivalent, presentPurchasingPower, purchasingPowerLoss], metadata);
}
export function calculateHousingLoan(input: FinanceInput<"housing-loan">, config: HousingLoanConfig) {
    const metadata = metadataFor(config, 'housing-loan');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'housing-loan', ['principal', 'monthlyRatePercent', 'termMonths', 'fileFeePercent']);
    for (const field of ['principal', 'monthlyRatePercent', 'termMonths', 'fileFeePercent'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], 0);
        if (error)
            configErrors.push(error);
    }
    if (configErrors.length > 0 || configRecord === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['principal', 'monthlyRatePercent', 'termMonths'], ['fileFeePercent'], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    const fileFeePercent = record.fileFeePercent ?? 0;
    for (const [field, value, range, integer] of [
        ['principal', record.principal, config.principal, false],
        ['monthlyRatePercent', record.monthlyRatePercent, config.monthlyRatePercent, false],
        ['termMonths', record.termMonths, config.termMonths, true],
        ['fileFeePercent', fileFeePercent, config.fileFeePercent, false],
    ] as const) {
        const error = boundedNumber(field, value, range, { integer });
        if (error)
            errors.push(error);
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const principal = record.principal;
    const monthlyRatePercent = record.monthlyRatePercent === 0
        ? 0
        : record.monthlyRatePercent;
    const termMonths = record.termMonths;
    const monthlyPayment = annuityPayment(principal, monthlyRatePercent / 100, termMonths);
    const totalPayment = monthlyRatePercent === 0
        ? principal
        : monthlyPayment * termMonths;
    const totalInterest = monthlyRatePercent === 0
        ? 0
        : totalPayment - principal;
    const normalizedFileFeePercent = fileFeePercent === 0 ? 0 : fileFeePercent;
    const fileFee = principal * normalizedFileFeePercent / 100;
    const overallCost = totalPayment + fileFee;
    return finiteResult({ principal: normalizeSignedZero(principal), monthlyRatePercent: normalizeSignedZero(monthlyRatePercent), termMonths: normalizeSignedZero(termMonths), fileFeePercent: normalizeSignedZero(normalizedFileFeePercent), monthlyPayment: normalizeSignedZero(monthlyPayment), totalInterest: normalizeSignedZero(totalInterest), totalPayment: normalizeSignedZero(totalPayment), fileFee: normalizeSignedZero(fileFee), overallCost: normalizeSignedZero(overallCost) }, [monthlyPayment, totalInterest, totalPayment, fileFee, overallCost], metadata);
}
export function calculateConsumerLoan(input: FinanceInput<"consumer-loan">, config: ConsumerLoanConfig) {
    const metadata = metadataFor(config, 'consumer-loan');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'consumer-loan', ['principal', 'monthlyRatePercent', 'termMonths', 'policy']);
    for (const field of ['principal', 'monthlyRatePercent', 'termMonths'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], 0, field === 'principal' ? { exclusiveMinimum: true } : {});
        if (error)
            configErrors.push(error);
    }
    const policy = recordOf(configRecord?.policy);
    configErrors.push(...exactKeyErrors(policy, ['bsmvRate', 'kkdfRate', 'maximumTermTiers'], [], 'config.policy', 'INVALID_CONFIG'));
    for (const field of ['bsmvRate', 'kkdfRate'] as const) {
        const error = policyNumber(policy, field, 0, 1);
        if (error)
            configErrors.push(error);
    }
    const tierInput = policy?.maximumTermTiers;
    const tiers: { upToPrincipal: number | null; maximumMonths: number }[] = [];
    let previousThreshold = 0;
    const maximumTermTiers = tierInput !== undefined && isArray(tierInput) && tierInput.length > 0 ? snapshotArray(tierInput, (tier, index, length) => {
        configErrors.push(...exactKeyErrors(tier, ['upToPrincipal', 'maximumMonths'], [], `config.policy.maximumTermTiers[${index}]`, 'INVALID_CONFIG'));
        const tierRecord = recordOf(tier);
        const threshold = tierRecord?.upToPrincipal;
        const months = tierRecord?.maximumMonths;
        const isLast = index === length - 1;
        if ((isLast ? threshold !== null : typeof threshold !== 'number' || !isFiniteNumber(threshold) || threshold <= previousThreshold) ||
            typeof months !== 'number' || !isFiniteNumber(months) ||
            !Number.isInteger(months) ||
            months < 1) {
            configErrors.push(invalidConfig(`config.policy.maximumTermTiers[${index}]`));
        }
        if ((threshold === null || typeof threshold === 'number' && isFiniteNumber(threshold)) && typeof months === 'number' && isFiniteNumber(months))
            tiers.push({ upToPrincipal: threshold, maximumMonths: months });
        if (typeof threshold === 'number' && isFiniteNumber(threshold))
            previousThreshold = threshold;
    }) : null;
    if (!maximumTermTiers) {
        configErrors.push(invalidConfig('config.policy.maximumTermTiers'));
    }
    if (configErrors.length > 0 || configRecord === null || policy === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['principal', 'monthlyRatePercent', 'termMonths', 'mode'], [], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    for (const [field, range, integer] of [
        ['principal', config.principal, false],
        ['monthlyRatePercent', config.monthlyRatePercent, false],
        ['termMonths', config.termMonths, true],
    ] as const) {
        const error = boundedNumber(field, record[field], range, { integer });
        if (error)
            errors.push(error);
    }
    if (record.mode !== 'plain' && record.mode !== 'tax-inclusive-interest') {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Kredi vergi modu desteklenmiyor.' });
    }
    const principalCandidate = record.principal;
    const termMonthsCandidate = record.termMonths;
    if (isFiniteNumber(principalCandidate) &&
        isFiniteNumber(termMonthsCandidate)) {
        const applicableTier = tiers.find((tier) => tier.upToPrincipal === null || principalCandidate <= tier.upToPrincipal);
        if (applicableTier === undefined || termMonthsCandidate > applicableTier.maximumMonths) {
            errors.push({
                field: 'termMonths',
                code: 'OUT_OF_RANGE',
                message: 'Vade, kredi tutarına uygulanan doğrulanmış azami süreyi aşıyor.',
            });
        }
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const principal = record.principal;
    const monthlyRatePercent = record.monthlyRatePercent === 0
        ? 0
        : record.monthlyRatePercent;
    const termMonths = record.termMonths;
    const mode = record.mode;
    const taxFactor = mode === 'tax-inclusive-interest'
        ? 1 + config.policy.bsmvRate + config.policy.kkdfRate
        : 1;
    const effectiveMonthlyRatePercent = monthlyRatePercent === 0
        ? 0
        : monthlyRatePercent * taxFactor;
    const monthlyPayment = annuityPayment(principal, effectiveMonthlyRatePercent / 100, termMonths);
    const totalPayment = effectiveMonthlyRatePercent === 0
        ? principal
        : monthlyPayment * termMonths;
    const totalInterestAndTaxes = effectiveMonthlyRatePercent === 0
        ? 0
        : totalPayment - principal;
    const bsmvAmount = mode === 'tax-inclusive-interest' && totalInterestAndTaxes !== 0
        ? totalInterestAndTaxes * config.policy.bsmvRate / taxFactor
        : 0;
    const kkdfAmount = mode === 'tax-inclusive-interest' && totalInterestAndTaxes !== 0
        ? totalInterestAndTaxes * config.policy.kkdfRate / taxFactor
        : 0;
    const pureInterestAmount = totalInterestAndTaxes === 0
        ? 0
        : totalInterestAndTaxes - bsmvAmount - kkdfAmount;
    return finiteResult({ principal: normalizeSignedZero(principal), monthlyRatePercent: normalizeSignedZero(monthlyRatePercent), termMonths: normalizeSignedZero(termMonths), mode, effectiveMonthlyRatePercent: normalizeSignedZero(effectiveMonthlyRatePercent), monthlyPayment: normalizeSignedZero(monthlyPayment), totalInterestAndTaxes: normalizeSignedZero(totalInterestAndTaxes), pureInterestAmount: normalizeSignedZero(pureInterestAmount), totalPayment: normalizeSignedZero(totalPayment), bsmvAmount: normalizeSignedZero(bsmvAmount), kkdfAmount: normalizeSignedZero(kkdfAmount) }, [effectiveMonthlyRatePercent, monthlyPayment, totalInterestAndTaxes, pureInterestAmount, totalPayment, bsmvAmount, kkdfAmount], metadata);
}
export function calculateDepreciationPlan(input: FinanceInput<"depreciation-plan">, config: DepreciationConfig) {
    const metadata = metadataFor(config, 'depreciation-plan');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'depreciation-plan', ['cost', 'policy']);
    const costRangeError = validateRange('config.cost', configRecord?.cost, 0, { exclusiveMinimum: true });
    if (costRangeError)
        configErrors.push(costRangeError);
    const policy = recordOf(configRecord?.policy);
    configErrors.push(...exactKeyErrors(policy, ['decliningBalanceMultiplier', 'maximumDecliningRate'], [], 'config.policy', 'INVALID_CONFIG'));
    const multiplierError = policyNumber(policy, 'decliningBalanceMultiplier', 1, 10);
    const capError = policyNumber(policy, 'maximumDecliningRate', Number.MIN_VALUE, 1);
    if (multiplierError)
        configErrors.push(multiplierError);
    if (capError)
        configErrors.push(capError);
    if (configErrors.length > 0 || configRecord === null || policy === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['cost', 'usefulLifeYears', 'method'], ['decliningBalanceEligibilityConfirmed'], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    const costError = boundedNumber('cost', record.cost, config.cost);
    if (costError)
        errors.push(costError);
    if (record.method !== 'straight-line' && record.method !== 'declining-balance') {
        errors.push({ field: 'method', code: 'UNSUPPORTED_VALUE', message: 'Amortisman yöntemi desteklenmiyor.' });
    }
    if (Object.prototype.hasOwnProperty.call(record, 'decliningBalanceEligibilityConfirmed') &&
        typeof record.decliningBalanceEligibilityConfirmed !== 'boolean') {
        errors.push({
            field: 'decliningBalanceEligibilityConfirmed',
            code: 'UNSUPPORTED_VALUE',
            message: 'Uygunluk doğrulaması boolean olmalıdır.',
        });
    }
    else if (record.method === 'declining-balance' && record.decliningBalanceEligibilityConfirmed !== true) {
        errors.push({
            field: 'decliningBalanceEligibilityConfirmed',
            code: 'REQUIRED',
            message: 'Azalan bakiyeler yöntemi için bilanço esası, kıymet ve yöntem geçmişi uygunluğu doğrulanmalıdır.',
        });
    }
    const lifeError = boundedNumber('usefulLifeYears', record.usefulLifeYears, [1, 100], { integer: true });
    if (lifeError)
        errors.push(lifeError);
    if (errors.length > 0)
        return failure(errors, metadata);
    const cost = record.cost;
    const usefulLifeYears = record.usefulLifeYears;
    const method = record.method;
    const rate = method === 'straight-line'
        ? 1 / usefulLifeYears
        : Math.min(config.policy.decliningBalanceMultiplier / usefulLifeYears, config.policy.maximumDecliningRate);
    let netBookValue = cost;
    let cumulativeDepreciation = 0;
    const schedule = [];
    for (let year = 1; year <= usefulLifeYears; year += 1) {
        const expense = method === 'straight-line'
            ? (year === usefulLifeYears ? netBookValue : cost / usefulLifeYears)
            : (year === usefulLifeYears ? netBookValue : Math.min(netBookValue, netBookValue * rate));
        cumulativeDepreciation += expense;
        netBookValue = Math.max(0, cost - cumulativeDepreciation);
        schedule.push({ year: normalizeSignedZero(year), expense: normalizeSignedZero(expense), cumulativeDepreciation: normalizeSignedZero(cumulativeDepreciation), netBookValue: normalizeSignedZero(netBookValue) });
    }
    const firstYear = schedule[0];
    if (firstYear === undefined) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Amortisman planı üretilemedi.' }], metadata);
    }
    return finiteResult({
        cost: normalizeSignedZero(cost),
        usefulLifeYears: normalizeSignedZero(usefulLifeYears),
        method,
        depreciationRatePercent: normalizeSignedZero(rate * 100),
        firstYearExpense: normalizeSignedZero(firstYear.expense),
        totalDepreciation: normalizeSignedZero(cumulativeDepreciation),
        schedule,
    }, [rate, firstYear.expense, cumulativeDepreciation, ...schedule.flatMap((row) => [row.expense, row.cumulativeDepreciation, row.netBookValue])], metadata);
}
export function calculateDiscount(input: DiscountInput, config: DiscountConfig) {
    const metadata = metadataFor(config, 'discount');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'discount', ['price', 'discountPercent']);
    for (const field of ['price', 'discountPercent'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], 0);
        if (error)
            configErrors.push(error);
    }
    if (configErrors.length > 0 || configRecord === null)
        return failure(configErrors, metadata);
    const inputRecord = recordOf(input);
    const mode = inputRecord?.mode;
    const required = mode === 'price-from-rate'
        ? ['mode', 'originalPrice', 'discountPercent']
        : mode === 'rate-from-price'
            ? ['mode', 'originalPrice', 'discountedPrice']
            : ['mode', 'originalPrice'];
    const checked = requireInput(input, required, [], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    const priceError = boundedNumber('originalPrice', record.originalPrice, config.price);
    if (priceError)
        errors.push(priceError);
    if (mode === 'price-from-rate') {
        const error = boundedNumber('discountPercent', record.discountPercent, config.discountPercent);
        if (error)
            errors.push(error);
    }
    else if (mode === 'rate-from-price') {
        const error = boundedNumber('discountedPrice', record.discountedPrice, [0, config.price[1]]);
        if (error)
            errors.push(error);
        const discountedPrice = record.discountedPrice;
        if (typeof discountedPrice === 'number' && isFiniteNumber(discountedPrice) && isFiniteNumber(record.originalPrice) && discountedPrice > record.originalPrice) {
            errors.push({ field: 'discountedPrice', code: 'OUT_OF_RANGE', message: 'İndirimli fiyat ilk fiyattan büyük olamaz.' });
        }
    }
    else {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'İndirim hesap türü desteklenmiyor.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const originalPrice = record.originalPrice;
    const discountPercent = mode === 'price-from-rate'
        ? record.discountPercent ?? Number.NaN
        : (originalPrice - (record.discountedPrice ?? Number.NaN)) / originalPrice * 100;
    const discountAmount = originalPrice * discountPercent / 100;
    const discountedPrice = originalPrice - discountAmount;
    return finiteResult({ mode: mode, originalPrice: normalizeSignedZero(originalPrice), discountPercent: normalizeSignedZero(discountPercent), discountAmount: normalizeSignedZero(discountAmount), discountedPrice: normalizeSignedZero(discountedPrice) }, [discountPercent, discountAmount, discountedPrice], metadata);
}
export function calculateRentIncrease(input: FinanceInput<"rent-increase">, config: RentIncreaseConfig) {
    const metadata = metadataFor(config, 'rent-increase');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'rent-increase', ['currentMonthlyRent', 'twelveMonthCpiAveragePercent', 'policy']);
    for (const field of ['currentMonthlyRent', 'twelveMonthCpiAveragePercent'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], 0);
        if (error)
            configErrors.push(error);
    }
    const policy = recordOf(configRecord?.policy);
    configErrors.push(...exactKeyErrors(policy, ['monthsPerYear'], [], 'config.policy', 'INVALID_CONFIG'));
    const monthsError = policyNumber(policy, 'monthsPerYear', 1, 100, true);
    if (monthsError)
        configErrors.push(monthsError);
    if (configErrors.length > 0 || configRecord === null || policy === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['currentMonthlyRent', 'twelveMonthCpiAveragePercent'], [], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    const rentError = boundedNumber('currentMonthlyRent', record.currentMonthlyRent, config.currentMonthlyRent);
    const rateError = boundedNumber('twelveMonthCpiAveragePercent', record.twelveMonthCpiAveragePercent, config.twelveMonthCpiAveragePercent);
    if (rentError)
        errors.push(rentError);
    if (rateError)
        errors.push(rateError);
    if (errors.length > 0)
        return failure(errors, metadata);
    const currentMonthlyRent = record.currentMonthlyRent;
    const twelveMonthCpiAveragePercent = record.twelveMonthCpiAveragePercent;
    const monthlyIncrease = currentMonthlyRent * twelveMonthCpiAveragePercent / 100;
    const newMonthlyRent = currentMonthlyRent + monthlyIncrease;
    const annualExtraCost = monthlyIncrease * config.policy.monthsPerYear;
    return finiteResult({ currentMonthlyRent: normalizeSignedZero(currentMonthlyRent), twelveMonthCpiAveragePercent: normalizeSignedZero(twelveMonthCpiAveragePercent), monthlyIncrease: normalizeSignedZero(monthlyIncrease), newMonthlyRent: normalizeSignedZero(newMonthlyRent), annualExtraCost: normalizeSignedZero(annualExtraCost) }, [monthlyIncrease, newMonthlyRent, annualExtraCost], metadata);
}
export function calculatePosCommission(input: FinanceInput<"pos-commission">, config: PosCommissionConfig) {
    const metadata = metadataFor(config, 'pos-commission');
    const configRecord = recordOf(config);
    const configErrors = validateConfigEnvelope(config, 'pos-commission', ['amount', 'commissionPercent', 'supportedInstallments', 'policy']);
    for (const field of ['amount', 'commissionPercent'] as const) {
        const error = validateRange(`config.${field}`, configRecord?.[field], 0, field === 'amount' ? { exclusiveMinimum: true } : { maximumCeiling: normalizeSignedZero(100) });
        if (error)
            configErrors.push(error);
    }
    if (configRecord === null || !isArray(configRecord.supportedInstallments) ||
        configRecord.supportedInstallments.length === 0 ||
        new Set(configRecord.supportedInstallments).size !== configRecord.supportedInstallments.length ||
        configRecord.supportedInstallments.some((value: number) => !isFiniteNumber(value) || !Number.isInteger(value) || value < 1)) {
        configErrors.push(invalidConfig('config.supportedInstallments'));
    }
    const policy = recordOf(configRecord?.policy);
    configErrors.push(...exactKeyErrors(policy, ['bsmvRate'], [], 'config.policy', 'INVALID_CONFIG'));
    const bsmvError = policyNumber(policy, 'bsmvRate', 0, 1);
    if (bsmvError)
        configErrors.push(bsmvError);
    if (configErrors.length > 0 || configRecord === null || policy === null)
        return failure(configErrors, metadata);
    const checked = requireInput(input, ['amount', 'installmentCount', 'commissionPercent', 'mode'], [], metadata);
    if (isFailureResult(checked))
        return checked;
    const { record, errors } = checked;
    const amountError = boundedNumber('amount', record.amount, config.amount);
    const rateError = boundedNumber('commissionPercent', record.commissionPercent, config.commissionPercent);
    if (amountError)
        errors.push(amountError);
    if (rateError)
        errors.push(rateError);
    if (!config.supportedInstallments.includes(record.installmentCount)) {
        errors.push({ field: 'installmentCount', code: 'UNSUPPORTED_VALUE', message: 'Taksit sayısı desteklenmiyor.' });
    }
    if (record.mode !== 'commission-only' && record.mode !== 'commission-plus-bsmv') {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'POS vergi modu desteklenmiyor.' });
    }
    if (record.mode === 'commission-plus-bsmv' &&
        isFiniteNumber(record.commissionPercent) &&
        record.commissionPercent > 100 / (1 + config.policy.bsmvRate)) {
        errors.push({
            field: 'commissionPercent',
            code: 'OUT_OF_RANGE',
            message: 'Komisyon ve BSMV toplamı işlem tutarını aşamaz.',
        });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const amount = record.amount;
    const installmentCount = record.installmentCount;
    const commissionPercent = record.commissionPercent;
    const mode = record.mode;
    const commissionAmount = amount * commissionPercent / 100;
    const bsmvAmount = mode === 'commission-plus-bsmv' ? commissionAmount * config.policy.bsmvRate : 0;
    const rawTotalDeduction = commissionAmount + bsmvAmount;
    const epsilon = Number.EPSILON * Math.max(1, amount) * 8;
    const totalDeduction = Math.abs(rawTotalDeduction - amount) <= epsilon
        ? amount
        : rawTotalDeduction;
    const rawNetAmount = amount - totalDeduction;
    const netAmount = Math.abs(rawNetAmount) <= epsilon ? 0 : rawNetAmount;
    const netAllocationPerInstallment = netAmount / installmentCount;
    return finiteResult({ amount: normalizeSignedZero(amount), installmentCount: normalizeSignedZero(installmentCount), commissionPercent: normalizeSignedZero(commissionPercent), mode, commissionAmount: normalizeSignedZero(commissionAmount), bsmvAmount: normalizeSignedZero(bsmvAmount), totalDeduction: normalizeSignedZero(totalDeduction), netAmount: normalizeSignedZero(netAmount), netAllocationPerInstallment: normalizeSignedZero(netAllocationPerInstallment) }, [commissionAmount, bsmvAmount, totalDeduction, netAmount, netAllocationPerInstallment], metadata);
}
